import styled from "styled-components";
type SidebarProps = {
  $isSidebarCollapsed?: boolean;
};
// Main Container
export const WalletContainer = styled.div<SidebarProps>`
  width: 100%;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 678px) {
    padding: ${({ theme, $isSidebarCollapsed }) =>
      $isSidebarCollapsed
        ? theme.spacing.md
        : `${theme.spacing.xs} ${theme.spacing.sm}`};
  }
`;

// Tab Components
export const TabContainer = styled.div`
  display: flex;
  width: 100%;
  background-color: transparent;
  border-bottom: none;

  @media (min-width: 768px) {
    background-color: ${({ theme }) => theme.colors.lightGray};
    border-radius: 12px;
    padding: 4px;
    margin-bottom: 24px;
  }
`;

export const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 16px 20px;
  border: none;
  background-color: ${(props) => (props.$active ? props.theme.colors.primary : "transparent")};
  color: ${(props) => (props.$active ? props.theme.colors.white : props.theme.colors.primary)};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;

  &:first-child {
    border-radius: 0;
  }

  &:last-child {
    border-radius: 0;
  }

  @media (min-width: 768px) {
    background-color: ${(props) => (props.$active ? props.theme.colors.primary : "transparent")};
    color: ${(props) => (props.$active ? props.theme.colors.white : "#64748b")};
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 15px;

    &:hover {
      background-color: ${(props) => (props.$active ? props.theme.colors.primaryDark : props.theme.colors.underline)};
    }
  }

  @media (min-width: 1024px) {
    padding: 14px 32px;
    font-size: 16px;
  }
`;

// Content Container
export const ContentContainer = styled.div`
  width: 100%;
  padding: 0;

  @media (min-width: 768px) {
    padding: 0;
  }
`;

// Overview Section
export const OverviewSection = styled.div`
  padding: 20px 16px;

  @media (min-width: 768px) {
    padding: 0;
  }
`;

export const BalanceCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 24px 20px;

  @media (min-width: 768px) {
    padding: 32px;
    border-radius: 16px;
  }

  @media (min-width: 1024px) {
    padding: 40px;
    max-width: 600px;
    margin: 0 auto;
  }
`;

export const BalanceTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin: 0 0 16px 0;

  @media (min-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 24px;
    margin-bottom: 24px;
  }
`;

export const BalanceAmount = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    font-size: 42px;
    margin-bottom: 40px;
  }

  @media (min-width: 1024px) {
    font-size: 48px;
    margin-bottom: 48px;
  }
`;

export const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 20px;
  }

  @media (min-width: 768px) {
    margin-bottom: 40px;
    gap: 24px;
  }

  @media (min-width: 1024px) {
    margin-bottom: 48px;
  }
`;

export const BalanceItem = styled.div`
  flex: 1;
  text-align: center;

  @media (max-width: 480px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
  }

  @media (min-width: 768px) {
    text-align: center;
  }
`;

export const BalanceLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;

  @media (max-width: 480px) {
    margin-bottom: 0;
  }

  @media (min-width: 768px) {
    font-size: 15px;
    margin-bottom: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 16px;
  }
`;

export const BalanceValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;

  @media (min-width: 768px) {
    font-size: 18px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
  }
`;

export const PayoutButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    padding: 18px 32px;
    font-size: 17px;
    border-radius: 14px;
  }

  @media (min-width: 1024px) {
    padding: 20px 40px;
    font-size: 18px;
  }
`;

// Transaction History Section
export const TransactionSection = styled.div`
  padding: 20px 16px;

  @media (min-width: 768px) {
    padding: 24px 32px;
    max-width: 1000px;
    margin: 0 auto;
  }

  @media (min-width: 1024px) {
    padding: 32px 40px;
    max-width: 1200px;
  }
`;

export const SearchContainer = styled.div`
  margin-bottom: 16px;

  @media (min-width: 768px) {
    margin-bottom: 24px;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  background-color: ${({ theme }) => theme.colors.white};
  color: #64748b;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(227, 148, 53, 0.1);
  }

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 15px;
    max-width: 400px;
  }

  @media (min-width: 1024px) {
    max-width: 500px;
  }
`;

export const FilterContainer = styled.div`
  margin-bottom: 16px;
`;

export const FilterLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    margin-bottom: 0;
    font-size: 15px;
    min-width: 60px;
  }
`;

export const FilterDropdown = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23E39435' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(227, 148, 53, 0.1);
  }

  @media (min-width: 768px) {
    width: 200px;
    padding: 14px 42px 14px 18px;
    font-size: 15px;
    background-position: right 14px center;
  }
`;

export const FilterOption = styled.option`
  padding: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  color: #1e293b;

  &:hover {
    background-color: #f8fafc;
  }

  &:checked {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const DateSearchContainer = styled.div`
  margin-bottom: 24px;

  @media (min-width: 768px) {
    margin-bottom: 32px;
  }
`;

export const DateSearchToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    font-size: 15px;
    margin-bottom: 18px;
  }
`;

export const DateInputContainer = styled.div`
  margin-bottom: 16px;

  @media (min-width: 768px) {
    margin-bottom: 18px;
  }
`;

export const DateInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  background-color: ${({ theme }) => theme.colors.white};
  color: #64748b;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(227, 148, 53, 0.1);
  }

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 15px;
  }
`;

export const SearchButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  @media (min-width: 768px) {
    font-size: 15px;
    width: 120px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 16px;

  @media (min-width: 768px) {
    padding: 80px 40px;
    font-size: 17px;
  }

  @media (min-width: 1024px) {
    padding: 100px 60px;
    font-size: 18px;
  }
`;

// Payout Modal Components
export const PayoutModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

export const ModalContent = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    padding: 32px;
    border-radius: 20px;
  }
`;

export const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 24px 0;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 22px;
    margin-bottom: 32px;
  }
`;

export const ModalBalanceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 16px;

  div:first-child {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
  }

  div:last-child {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media (min-width: 768px) {
    padding: 18px 0;
    margin-bottom: 20px;

    div:first-child {
      font-size: 15px;
    }

    div:last-child {
      font-size: 17px;
    }
  }
`;

export const ModalInputContainer = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 8px;
  }

  @media (min-width: 768px) {
    margin-bottom: 32px;

    label {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

export const ModalInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  background-color: ${({ theme }) => theme.colors.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(227, 148, 53, 0.1);
  }

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 15px;
  }
`;

export const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  @media (min-width: 768px) {
    gap: 16px;
    margin-top: 32px;
  }
`;

export const ModalButton = styled.button<{ $variant: "primary" | "secondary" }>`
  flex: 1;
  padding: 16px 24px;
  border: ${(props) =>
    props.$variant === "primary" ? "none" : "1px solid #d1d5db"};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "primary" ? props.theme.colors.primary : props.theme.colors.white};
  color: ${(props) => (props.$variant === "primary" ? props.theme.colors.white : props.theme.colors.text)};

  @media (min-width: 768px) {
    padding: 18px 32px;
    font-size: 15px;
  }
`;

export const DateSearch = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width:100%;

  @media (min-width: 768px) {
    flex-direction: row;
    width:auto;
  }
`;

export const EditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

// Payout Modal helper components (to replace inline styles)
export const ModalCenterHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

export const ModalMutedText = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.disabled};
  margin-bottom: 8px;
`;

export const ModalGreenAmount = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const ModalTwoColSummary = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 24px;
`;

export const ModalSummaryItem = styled.div`
  text-align: center;
  flex: 1;
`;

export const ModalSummaryLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.disabled};
  margin-bottom: 8px;
`;

export const ModalSummaryValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

export const ModalFieldLabelOrange = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
`;

// New Figma Design Components
export const FigmaWalletContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 16px 0px 16px 0px;
  gap: 15px;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;

  @media (min-width: 768px) {
    max-width: 600px;
    padding: 60px 40px 0px;
    gap: 20px;
  }

  @media (min-width: 1024px) {
    max-width: 800px;
    padding: 80px 60px 0px;
    gap: 25px;
  }
`;

export const FigmaBalanceCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  gap: 20px;
  width: 100%;
  max-width: 400px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 20px;
  align-self: stretch;

  @media (min-width: 768px) {
    padding: 20px;
    gap: 25px;
    border-radius: 25px;
  }

  @media (min-width: 1024px) {
    padding: 30px;
    gap: 30px;
  }
`;

export const FigmaAvailableBalance = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 15px;
  width: 100%;
  align-self: stretch;
`;

export const FigmaBalanceTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 21px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 23px;
  }
`;

export const FigmaBalanceAmount = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.secondary};

  @media (min-width: 768px) {
    font-size: 28px;
    line-height: 32px;
  }

  @media (min-width: 1024px) {
    font-size: 32px;
    line-height: 36px;
  }
`;

export const FigmaPendingBalanceRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 15px;
  }

  @media (min-width: 1024px) {
    gap: 20px;
  }
`;

export const FigmaBalanceItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 15px;
  width: 185px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 18px;
  }

  @media (min-width: 1024px) {
    gap: 20px;
  }
`;

export const FigmaBalanceItemTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaBalanceDivider = styled.div`
  width: 100%;
  height: 0px;
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

export const FigmaBalanceItemValue = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.disabled};
  text-align: center;

  @media (min-width: 768px) {
    font-size: 26px;
    line-height: 30px;
  }

  @media (min-width: 1024px) {
    font-size: 28px;
    line-height: 32px;
  }
`;

export const FigmaBalanceItemValueReport = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;

  @media (min-width: 768px) {
    font-size: 26px;
    line-height: 30px;
  }

  @media (min-width: 1024px) {
    font-size: 28px;
    line-height: 32px;
  }
`;

export const FigmaRequestButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 15px;
  gap: 10px;
  width: 100%;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 25px;
  border: none;
  cursor: pointer;
  align-self: stretch;

  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.white};


  @media (min-width: 768px) {
    padding: 12px 18px;
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    padding: 14px 20px;
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaTransactionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 23px;
  width: 100%;
  max-width: 400px;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 28px;
  }

  @media (min-width: 1024px) {
    gap: 32px;
  }
`;

export const FigmaTransactionHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 15px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 20px;
  }

  @media (min-width: 1024px) {
    gap: 25px;
  }
`;

export const FigmaTransactionTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;

  @media (min-width: 768px) {
    font-size: 26px;
    line-height: 30px;
  }

  @media (min-width: 1024px) {
    font-size: 28px;
    line-height: 32px;
  }
`;

export const FigmaViewAllButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 15px;
  gap: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  border: none;
  cursor: pointer;

  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    background: #6a9e3a;
  }

  &:active {
    background: #5e8c33;
  }

  @media (min-width: 768px) {
    padding: 12px 18px;
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    padding: 14px 20px;
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaTransactionList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 12px;
  }

  @media (min-width: 1024px) {
    gap: 15px;
  }
`;

export const FigmaTransactionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 10px;
  width: 100%;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 15px;
  align-self: stretch;

  @media (min-width: 768px) {
    border-radius: 18px;
  }

  @media (min-width: 1024px) {
    border-radius: 20px;
  }
`;

export const FigmaTransactionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 23px;
  gap: 8px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    padding: 26px;
    gap: 10px;
  }

  @media (min-width: 1024px) {
    padding: 30px;
    gap: 12px;
  }
`;

export const FigmaTransactionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 23px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 28px;
  }

  @media (min-width: 1024px) {
    gap: 32px;
  }
`;

export const FigmaTransactionInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 12px;
  }

  @media (min-width: 1024px) {
    gap: 15px;
  }
`;

export const FigmaTransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 7px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 8px;
  }

  @media (min-width: 1024px) {
    gap: 10px;
  }
`;

export const FigmaTransactionSource = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaTransactionTime = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.disabled};

  @media (min-width: 768px) {
    font-size: 13px;
    line-height: 15px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
    line-height: 16px;
  }
`;

export const FigmaTransactionAmountSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 27px;

  @media (min-width: 768px) {
    gap: 30px;
  }

  @media (min-width: 1024px) {
    gap: 35px;
  }
`;

export const FigmaCreditTag = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 5px 15px;
  gap: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 25px;

  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.white};

  @media (min-width: 768px) {
    padding: 6px 16px;
    font-size: 13px;
    line-height: 15px;
  }

  @media (min-width: 1024px) {
    padding: 7px 18px;
    font-size: 14px;
    line-height: 16px;
  }
`;

export const FigmaDebitTag = styled(FigmaCreditTag)`
  background: #d44b1f;
`;

export const FigmaTransactionAmount = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  min-width: 120px;

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;

// Transaction History Figma Design Components
export const FigmaTransactionHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 0px 16px 0px;
  gap: 15px;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  height: 100vh;
  position: relative;

  @media (min-width: 768px) {
    max-width: 600px;
    padding: 60px 40px 0px;
    gap: 20px;
  }

  @media (min-width: 1024px) {
    max-width: 800px;
    padding: 80px 60px 0px;
    gap: 25px;
  }
`;

export const FigmaHistoryTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  max-width: 400px;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 21px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 23px;
  }
`;

export const FigmaTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 15px;
  width: 100%;
  max-width: 400px;

  @media (min-width: 768px) {
    gap: 18px;
  }

  @media (min-width: 1024px) {
    gap: 20px;
  }
`;

export const FigmaTypeLabel = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  width: 100%;
  max-width: 400px;
  position: relative;
`;

export const FigmaDropdownButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 15px;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.primary};
  height: 44px;

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 17px;
    min-height: 48px;
  }
`;

export const FigmaDropdownText = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.primary};

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }
`;

export const FigmaDropdownIcon = styled.div<{ $isOpen?: boolean }>`
  width: 20px;
  height: 20px;
  position: relative;
  cursor: pointer;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};

  &::after {
    content: "";
    position: absolute;
    left: 25%;
    right: 25%;
    top: 37.5%;
    bottom: 37.5%;
    border: 2px solid ${({ theme }) => theme.colors.primary};
    border-left: none;
    border-top: none;
    transform: rotate(45deg);
  }
`;

export const FigmaDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  margin-top: 4px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

export const FigmaDropdownItem = styled.div`
  padding: 12px 15px;
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};

  &:hover {
    background: ${({ theme }) => theme.colors.lightGray};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    padding: 14px 18px;
    font-size: 17px;
  }
`;

export const FigmaDateSearchButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 24px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.white};
  height: 44px;
`;

export const FigmaAddVoucherButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px 8px 16px;
  gap: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.white};
`;

export const TabletWalletContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
`;

export const DesktopWalletContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
`;

export const TabletBalanceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const DesktopBalanceSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  min-width: 400px;
`;

export const TabletBalanceCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px;
  gap: 28px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 24px;
`;

export const DesktopBalanceCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 32px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 28px;
`;

export const TabletTransactionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const DesktopTransactionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  flex: 1;
  min-width: 0;
`;

export const TabletTransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const DesktopTransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const TabletTransactionTitle = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

export const DesktopTransactionTitle = styled.h2`
  font-weight: 700;
  font-size: 32px;
  line-height: 36px;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

export const TabletFilterSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

export const DesktopFilterSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
  margin-bottom: 8px;
`;

export const TabletDateSearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.colors.secondary};
  padding: 8px 16px;

  height: 44px;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.white};
  white-space: nowrap;
`;

export const DesktopDateSearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => theme.colors.secondary};
  height: 44px;
  border-radius: 24px;
  padding: 8px 16px;

  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
  white-space: nowrap;

  &:hover {
    background: #6a9e3a;
  }
`;

export const TabletTransactionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const DesktopTransactionList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

export const TabletTransactionList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const DesktopTransactionListOverview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 1920px) {
    display: grid;

    grid-template-columns: 1fr 1fr;
  }
`;

export const TabletTransactionItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 16px;
  gap: 12px;
  width: 100%;
`;

export const DesktopTransactionItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  width: 100%;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 16px;
`;

export const TabletRequestButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 24px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #6a9e3a;
    transform: translateY(-1px);
  }
`;

export const DesktopRequestButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 36px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 28px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #6a9e3a;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(118, 176, 65, 0.3);
  }
`;

export const TabletBalanceAmount = styled.div`
  font-weight: 700;
  font-size: 36px;
  line-height: 42px;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;
`;

export const DesktopBalanceAmount = styled.div`
  font-weight: 700;
  font-size: 42px;
  line-height: 48px;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: center;
`;

export const TabletBalanceRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
`;

export const DesktopBalanceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const TabletBalanceItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const DesktopBalanceItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 16px;
`;

export const NoRecordsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 65px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 15px;
`;

export const FigmaScrollContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    height: 600px;
  }

  @media (min-width: 1024px) {
    height: 700px;
  }
`;

export const FigmaTransactionHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 15px;
  width: 100%;

  @media (min-width: 768px) {
    gap: 18px;
  }

  @media (min-width: 1024px) {
    gap: 20px;
  }
`;

export const FigmaDateHeader = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.disabled};
  width: 100%;
`;

export const FigmaDateGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 5px;
  width: 100%;

  @media (min-width: 768px) {
    gap: 7px;
  }

  @media (min-width: 1024px) {
    gap: 10px;
  }
`;

export const FigmaHistoryTransactionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 10px;
  width: 100%;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 15px;
  align-self: stretch;

  @media (min-width: 768px) {
    border-radius: 18px;
  }

  @media (min-width: 1024px) {
    border-radius: 20px;
  }
`;

export const FigmaHistoryTransactionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 23px;
  gap: 8px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    padding: 26px;
    gap: 10px;
  }

  @media (min-width: 1024px) {
    padding: 30px;
    gap: 12px;
  }
`;

export const FigmaHistoryTransactionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 23px;
  width: 100%;
  align-self: stretch;

  @media (min-width: 768px) {
    gap: 28px;
  }

  @media (min-width: 1024px) {
    gap: 32px;
  }
`;

export const FigmaHistoryProfileFrame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 12px;
  }

  @media (min-width: 1024px) {
    gap: 15px;
  }
`;

export const FigmaHistoryProfile = styled.div<{ $show?: boolean }>`
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 7px;
  gap: 10px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 30px;

  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  color: ${({ theme }) => theme.colors.white};

  @media (min-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 26px;
    line-height: 30px;
  }

  @media (min-width: 1024px) {
    width: 48px;
    height: 48px;
    font-size: 28px;
    line-height: 32px;
  }
`;

export const FigmaHistoryTransactionDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 7px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 8px;
  }

  @media (min-width: 1024px) {
    gap: 10px;
  }
`;

export const FigmaHistoryTransactionSource = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  align-self: stretch;

  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;

export const FigmaHistoryTransactionTime = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.disabled};
  align-self: stretch;

  @media (min-width: 768px) {
    font-size: 13px;
    line-height: 15px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
    line-height: 16px;
  }
`;

export const FigmaHistoryAmountSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 27px;

  @media (min-width: 768px) {
    gap: 30px;
  }

  @media (min-width: 1024px) {
    gap: 35px;
  }
`;

export const FigmaHistoryCreditTag = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 5px 15px;
  gap: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 25px;

  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.white};

  @media (min-width: 768px) {
    padding: 6px 16px;
    font-size: 13px;
    line-height: 15px;
  }

  @media (min-width: 1024px) {
    padding: 7px 18px;
    font-size: 14px;
    line-height: 16px;
  }
`;

export const FigmaHistoryDebitTag = styled(FigmaHistoryCreditTag)`
  background: #d44b1f;
`;

export const FigmaHistoryTransactionAmount = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  @media (min-width: 768px) {
    font-size: 17px;
    line-height: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 21px;
  }
`;
